import { useState, useRef } from "react";
import LogManager from "../../utils/logManager";
import { compareAgendaItems, fromBackend } from "./agenda/agenda.utils";

/**
 * useInsightStore — manages real-time insight data pushed via WebSocket.
 *
 * Deduplicates by itemId using updatedAt as a monotonic version (at-least-once
 * delivery from DDB Stream means duplicates are expected).
 *
 * This is the single source of truth for the action-items list — the fetch/poll hook,
 * the optimistic mutations, and the header badge all read and write it. Every write is a
 * functional merge (upsert by itemId) or a targeted remove; there is no wholesale
 * array-replace, so an in-flight fetch can never evict an optimistic or freshly-pushed row.
 *
 * Maintains two collections:
 *   - actionItems: detected action items (newest first), keyed on `itemId`
 *   - agendaItems: agenda items (ordered by position), keyed on `id`
 *
 * Agenda pushes arrive in the backend shape (itemId/content/status) and are mapped to the frontend
 * row (id/title/state) with the agenda mapper, then kept sorted.
 *
 * @returns {{ actionItems, agendaItems, handleInsightMessage, upsertActionItems, removeActionItem, upsertAgendaItems, removeAgendaItem, clear, wsConnected, setWsConnected }}
 */
export const useInsightStore = () => {
  const [actionItems, setActionItems] = useState([]);
  const [agendaItems, setAgendaItems] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);

  // Track a single monotonic version per itemId for dedup.
  const versionMapRef = useRef(new Map());

  // Tombstones for recently-deleted itemIds (id -> deletedAt epoch ms). A delete is optimistic,
  // but a fetch/fallback-poll that was in flight when the delete happened can return the server
  // list BEFORE the delete committed and re-add the row via upsertActionItems (which has no
  // dedup). Tombstoning the id for a short window makes upsert drop it, so a stale read can't
  // resurrect a just-deleted item. itemIds are client-minted UUIDs (never reused), so a tombstone
  // can't block a legitimate new row; entries expire after the TTL to bound the map.
  const deletedIdsRef = useRef(new Map());
  const TOMBSTONE_TTL_MS = 60_000;
  const isTombstoned = (itemId) => {
    const at = deletedIdsRef.current.get(itemId);
    if (at == null) return false;
    if (Date.now() - at > TOMBSTONE_TTL_MS) {
      deletedIdsRef.current.delete(itemId);
      return false;
    }
    return true;
  };

  // One consistent version field per item, normalized to a numeric epoch (ms). Push messages
  // carry `updatedAt`; amended items carry `amendedAt`; freshly created / initial-fetch items
  // carry `detectedAt`. Pick the first present so the dedup map is always keyed on the same
  // comparable value — mixing `updatedAt` (compare) with `detectedAt` (seed) made comparisons
  // meaningless. Parse to epoch ms so `isDuplicate`'s `>=` is a numeric compare: comparing raw
  // ISO strings lexically breaks across precisions (e.g. "...00.500Z" < "...00Z" because "."
  // sorts before "Z"), which would wrongly suppress a newer ms-precise message in the same
  // second. Unparseable timestamps fall back to null (not deduped) rather than corrupting order.
  const versionOf = (obj) => {
    const raw = obj?.updatedAt ?? obj?.amendedAt ?? obj?.detectedAt ?? null;
    const ms = raw != null ? Date.parse(raw) : NaN;
    return Number.isNaN(ms) ? null : ms;
  };

  // Pure check — does NOT mutate the map. A missing version can't be deduped, so it's not a
  // duplicate. Recording is deferred until the message is actually applied (see below), so an
  // unrecognized action never poisons the map and suppresses a later legitimate message.
  const isDuplicate = (itemId, version) => {
    if (version == null) {
      return false;
    }
    const existing = versionMapRef.current.get(itemId);
    return existing != null && existing >= version;
  };

  // An agenda push carries a complete row, so it replaces rather than merges — the server is
  // authoritative and a field-by-field merge would only risk keeping stale local values.
  const agendaRow = (item) => fromBackend(item);

  // Applied after every agenda write so ordering tracks status changes as they arrive, not just
  // when a fetch happens to re-deliver the server's order.
  const sortedAgenda = (rows) => [...rows].sort(compareAgendaItems);

  const handleInsightMessage = (data) => {
    const { action, itemType, itemId, item } = data;

    // itemId + action are always required. `item` (the body) is required for created/amended,
    // but a backend `deleted` push carries only { itemId, action:"deleted" } — no body — and must
    // still reach the deleted handler below to remove the row for all viewers.
    if (!itemId || !action) {
      LogManager.warn("[InsightStore] insight_update missing required fields, ignoring");
      return;
    }
    if (action !== "deleted" && !item) {
      LogManager.warn("[InsightStore] insight_update missing required fields, ignoring");
      return;
    }

    // Drop any message for a just-deleted (tombstoned) item. Delete prunes the version-map entry,
    // so isDuplicate can no longer suppress a stale/redelivered `created`/`amended` for that id —
    // without this guard the WS insert path would resurrect the row (the fetch/poll path already
    // filters tombstoned ids). itemIds are client-minted UUIDs and never reused, so this can't
    // suppress a legitimate new row.
    if (isTombstoned(itemId)) {
      LogManager.info("[InsightStore] Ignoring message for deleted item:", itemId);
      return;
    }

    // Dedup: discard if we already have this version or newer. Prefer the message-level
    // version, falling back to the item's own timestamps (same field set as setInitial*).
    const version = versionOf(data) ?? versionOf(item);
    if (isDuplicate(itemId, version)) {
      LogManager.info("[InsightStore] Duplicate suppressed:", itemId);
      return;
    }

    let applied = true;
    if (action === "created" || action === "amended") {
      if (itemType === "action_item") {
        // Upsert: a redelivered `created`, or one for an item already seeded via
        // upsertActionItems, must update in place rather than insert a duplicate row
        // (which would collide on the itemId React key). The same path handles `amended`:
        // if an `amended` somehow arrives before its `created` (reordered delivery), a
        // plain map() would silently drop it — and because we still record its (newer)
        // version below, the later `created` would then be deduped and the item lost
        // entirely. Inserting instead keeps the item; the stale `created` is correctly
        // suppressed since we already hold the newer version.
        setActionItems((prev) =>
          prev.some((existing) => existing.itemId === itemId)
            ? prev.map((existing) => (existing.itemId === itemId ? { ...existing, ...item } : existing))
            : [item, ...prev]
        );
      } else {
        // Agenda: same upsert reasoning, but new rows append — agenda is ordered by position.
        setAgendaItems((prev) =>
          sortedAgenda(
            prev.some((existing) => existing.id === itemId)
              ? prev.map((existing) => (existing.id === itemId ? agendaRow(item) : existing))
              : [...prev, agendaRow(item)]
          )
        );
      }
    } else if (action === "agenda_status_changed" || action === "agenda_recap_added") {
      // Update-only (no insert): a status change can't safely materialize a full agenda row.
      // versionMapRef holds every itemId we've applied/seeded, so use it as the "is this item
      // known?" check without reading state in this callback. If unknown (status change arrived
      // before the item was seeded), leave applied=false so the version isn't recorded — that
      // would otherwise permanently suppress the later seed/message that establishes the row.
      //
      // Agenda rows are keyed on `id` (not `itemId` like action items) because the agenda feature
      // stores its own frontend shape — see upsertAgendaItems / removeAgendaItem below.
      //
      // A recap arrives as its own push a few seconds after the completed status, and takes the
      // same path: the payload is a complete row, so replacing is correct for both.
      if (versionMapRef.current.has(itemId)) {
        setAgendaItems((prev) =>
          sortedAgenda(prev.map((existing) => (existing.id === itemId ? agendaRow(item) : existing)))
        );
      } else {
        applied = false;
      }
    } else if (action === "deleted") {
      // A user deleted the item elsewhere — drop it. itemId is unique across types, so
      // filtering both collections is safe. Action items are keyed on itemId, agenda on id.
      setActionItems((prev) => prev.filter((existing) => existing.itemId !== itemId));
      setAgendaItems((prev) => prev.filter((existing) => existing.id !== itemId));
      // Prune the dedup version so a later item that reuses/reconciles this id isn't wrongly
      // suppressed, tombstone the id so a stale fetch/poll can't resurrect it, and skip
      // re-recording it below (applied=false).
      versionMapRef.current.delete(itemId);
      deletedIdsRef.current.set(itemId, Date.now());
      applied = false;
    } else {
      applied = false;
    }

    // Only commit the version once the message was actually applied.
    if (applied && version != null) {
      versionMapRef.current.set(itemId, version);
    }
  };

  // Merge a batch of action items into the store by itemId (functional, never a wholesale
  // replace): existing rows are shallow-merged in place (order preserved), genuinely-new rows
  // are prepended newest-first (matching the WS `created` handler and poll order). Used for the
  // initial seed, the fetch/fallback-poll reconciliation, and the optimistic mutations. Because
  // it only ever adds/updates the ids it's given, a stale server snapshot can't drop an
  // optimistic row or a row another writer just added — which is what caused the earlier
  // disappear/flash.
  //
  // Staleness guard: for an existing row, an incoming update whose version is OLDER than the one
  // recorded is skipped, so an in-flight fetch/fallback-poll that resolves after an optimistic
  // edit can't clobber the newer edit (last-writer-by-arrival). Pass `{ force: true }` to override
  // this — used by the mutation layer's success reconcile, which must apply a specific
  // (possibly older-versioned) row on purpose so the authoritative push can then win.
  const upsertActionItems = (incoming, { force = false } = {}) => {
    if (!incoming || incoming.length === 0) return;
    // Drop items that were just deleted locally — a stale fetch/poll must not resurrect them.
    // `force` bypasses this (and clears the tombstone) because the mutation layer's success
    // reconcile deliberately applies the authoritative server row; filtering it here would drop it.
    const items = incoming.filter((i) => i?.itemId && (force || !isTombstoned(i.itemId)));
    if (items.length === 0) return;
    if (force) items.forEach((i) => deletedIdsRef.current.delete(i.itemId));
    const isStale = (itemId, incomingV) => {
      if (force || incomingV == null) return false;
      const currentV = versionMapRef.current.get(itemId);
      return currentV != null && incomingV < currentV;
    };
    setActionItems((prev) => {
      const existingIds = new Set(prev.map((i) => i.itemId));
      const merged = prev.map((row) => {
        const update = items.find((i) => i.itemId === row.itemId);
        if (!update) return row;
        return isStale(row.itemId, versionOf(update)) ? row : { ...row, ...update };
      });
      const newRows = items.filter((i) => !existingIds.has(i.itemId));
      return [...newRows, ...merged];
    });
    items.forEach((item) => {
      const version = versionOf(item);
      if (version == null) return;
      // Record the version, but (unless forced) never downgrade a newer recorded one with a
      // stale snapshot — that would reopen the dedup window this guard closes.
      if (force) {
        versionMapRef.current.set(item.itemId, version);
      } else {
        const currentV = versionMapRef.current.get(item.itemId);
        if (currentV == null || version >= currentV) {
          versionMapRef.current.set(item.itemId, version);
        }
      }
    });
  };

  // Remove one action item by id and prune its dedup version, so a future add/reconcile of a
  // new item isn't suppressed by a stale version entry (the leak that let deleted rows reappear).
  // Tombstones the id by default (a delete is definitive), so a stale in-flight fetch/poll can't
  // resurrect the just-removed row for the TTL. Pass { tombstone: false } to remove WITHOUT
  // tombstoning — used by the add-failure rollback when the failure MIGHT have persisted server-side
  // (5xx/network), so a genuine WS `created` push can still land instead of being suppressed.
  const removeActionItem = (itemId, { tombstone = true } = {}) => {
    if (!itemId) return;
    setActionItems((prev) => prev.filter((i) => i.itemId !== itemId));
    versionMapRef.current.delete(itemId);
    if (tombstone) deletedIdsRef.current.set(itemId, Date.now());
  };

  /*
   Merge a batch of agenda items into the store by id (functional, never a wholesale replace).
   Existing rows are shallow-merged in place, new rows appended, then the list is re-sorted.
   `pruneMissing` treats `incoming` as a complete server snapshot and drops local rows absent from
   it — how a delete by another client reaches us when the socket was down. Only the fetch/poll
   path may pass it; an optimistic single-row upsert would wipe everything else. Exempt: rows with
   no version, which an older snapshot legitimately lacks, and every row when the snapshot is
  empty, since an empty read is indistinguishable from a failed one.
  */
  const upsertAgendaItems = (incoming, { force = false, pruneMissing = false } = {}) => {
    if (!incoming || incoming.length === 0) return;
    // `force` bypasses the tombstone too, not just the staleness check below: the delete-rollback
    // path calls removeAgendaItem (which tombstones the id) and then force-upserts the row back, so
    // filtering tombstoned ids here would silently drop the restore. Clear the tombstone as well,
    // otherwise an in-flight poll would re-drop the row for the rest of the TTL.
    const items = incoming.filter((i) => i?.id && (force || !isTombstoned(i.id)));
    if (items.length === 0) return;
    if (force) items.forEach((i) => deletedIdsRef.current.delete(i.id));
    const isStale = (itemId, incomingV) => {
      if (force || incomingV == null) return false;
      const currentV = versionMapRef.current.get(itemId);
      return currentV != null && incomingV < currentV;
    };
    setAgendaItems((prev) => {
      const incomingIds = new Set(items.map((i) => i.id));
      const retained = pruneMissing
        ? prev.filter((row) => incomingIds.has(row.id) || versionOf(row) == null)
        : prev;
      const existingIds = new Set(retained.map((i) => i.id));
      const merged = retained.map((row) => {
        const update = items.find((i) => i.id === row.id);
        if (!update) return row;
        return isStale(row.id, versionOf(update)) ? row : { ...row, ...update };
      });
      const newRows = items.filter((i) => !existingIds.has(i.id));
      return sortedAgenda([...merged, ...newRows]);
    });
    items.forEach((item) => {
      const version = versionOf(item);
      if (version == null) return;
      if (force) {
        versionMapRef.current.set(item.id, version);
      } else {
        const currentV = versionMapRef.current.get(item.id);
        if (currentV == null || version >= currentV) {
          versionMapRef.current.set(item.id, version);
        }
      }
    });
  };

  // Remove one agenda item by id and tombstone it so a stale fetch can't resurrect it.
  const removeAgendaItem = (itemId) => {
    if (!itemId) return;
    setAgendaItems((prev) => prev.filter((i) => i.id !== itemId));
    versionMapRef.current.delete(itemId);
    deletedIdsRef.current.set(itemId, Date.now());
  };

  const clear = () => {
    setActionItems([]);
    setAgendaItems([]);
    versionMapRef.current.clear();
    deletedIdsRef.current.clear();
  };

  return {
    actionItems,
    agendaItems,
    handleInsightMessage,
    upsertActionItems,
    removeActionItem,
    upsertAgendaItems,
    removeAgendaItem,
    clear,
    wsConnected,
    setWsConnected,
  };
};
